import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { fetchWithAuth } from '../../utils/api';
import './style.css';
import { EyeIcon, EyeSlashIcon } from '../../assets/icons/icons';
import { useConfig } from "../../contexts/ConfigContext";
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [formMode, setFormMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userType, setUserType] = useState('internal'); // internal | client
  const [ownerClientRoleId, setOwnerClientRoleId] = useState(null);

  // New state to toggle between Select and Input
  const [isNewOrg, setIsNewOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    row_id: '',
    payment: '',
    organization_id: '',
  });
  const [orgLocked, setOrgLocked] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const API_BASE = useConfig().config.apiBase;
  /* ================= FETCH DATA ================= */

  useEffect(() => {
    fetchProfiles();
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchOrganizations() {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setOrganizations(data || []);
    } catch (err) {
      console.error('Failed to load organizations:', err.message);
    }
  }

  // // Default-select an organization for client users when none chosen
  // useEffect(() => {
  //   if (!orgLocked && userType === 'client' && !isNewOrg && !formData.organization_id && organizations.length > 0) {
  //     setFormData((prev) => ({ ...prev, organization_id: organizations[0].id }));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [organizations, orgLocked, userType, isNewOrg, formData.organization_id]);

  async function fetchProfiles(showTableSpinner = true) {
    if (showTableSpinner) {
      setTableLoading(true);
    }
    setLoading(true);
    try {
      const { data: { user }, error: userErr } = await supabase.auth.getUser();
      if (userErr || !user) throw userErr || new Error('Not authenticated');
      setCurrentUserId(user.id);

      const { data: myProfile, error: myProfileErr } = await supabase
        .from('profile_table')
        .select('organization_id, row_id')
        .eq('id', user.id)
        .single();
      if (myProfileErr) throw myProfileErr;

      // Check if user is super_admin by checking their role
      if (myProfile?.row_id) {
        const { data: roleData } = await supabase
          .from('role_table')
          .select('role_name')
          .eq('id', myProfile.row_id)
          .single();

        if (roleData?.role_name?.toLowerCase() === 'super_admin') {
          setIsSuperAdmin(true);
        }
      }

      const orgId = myProfile?.organization_id;
      if (orgId) {
        setFormData((prev) => ({ ...prev, organization_id: orgId }));
        setOrgLocked(true);
      } else {
        setOrgLocked(false);
      }

      let profilesQuery = supabase
        .from('profile_table')
        .select('id, username, payment, row_id, created_at, organization_id')
        .order('created_at', { ascending: false });

      if (orgId) {
        profilesQuery = profilesQuery.eq('organization_id', orgId);
      }

      const { data: profiles, error: profileError } = await profilesQuery;

      if (profileError) throw profileError;

      const profileIds = profiles.map((p) => p.id);
      let emailById = {};
      if (profileIds.length) {
        const { data: emailRows, error: emailErr } = await supabase
          .from('users')
          .select('id, email')
          .in('id', profileIds);
        if (!emailErr && emailRows) {
          emailById = emailRows.reduce((acc, row) => {
            acc[row.id] = row.email;
            return acc;
          }, {});
        }
      }

      const usersWithEmail = profiles.map((profile) => ({
        ...profile,
        email: emailById[profile.id] || 'N/A',
      }));

      // Filter out soft-deleted accounts (emails mutated to deleted_...)
      const activeUsers = usersWithEmail.filter(u => {
        const email = (u.email || '').toLowerCase();
        return !email.startsWith('deleted_');
      });

      setUsers(activeUsers || []);
      // Fetch roles for this user (for form dropdown)
      fetchRoles(user.id);
      // Fetch all roles for table display
      fetchAllRoles();
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }

  async function fetchRoles(userId) {
    try {
      // Get current user's organization
      const { data: myProfile } = await supabase
        .from('profile_table')
        .select('organization_id')
        .eq('id', userId)
        .single();

      const orgId = myProfile?.organization_id;

      if (orgId) {
        // If user has organization, get all users in that organization
        const { data: orgUsers } = await supabase
          .from('profile_table')
          .select('id')
          .eq('organization_id', orgId);

        const userIds = orgUsers?.map(u => u.id) || [userId];

        // Fetch roles created by any user in the organization
        const { data, error } = await supabase
          .from('role_table')
          .select('id, role_name')
          .in('created_by', userIds)
          .order('role_name');

        if (error) throw error;
        console.log("Roles for organization users:", data);
        setRoles((data || []).map(r => ({ id: r.id, name: r.role_name })));
      } else {
        // No organization - fetch roles created by this user AND by super_admin users

        // Get all super_admin role IDs
        const { data: superAdminRoles, error: roleErr } = await supabase
          .from('role_table')
          .select('id')
          .eq('role_name', 'super_admin');

        if (!roleErr && superAdminRoles && superAdminRoles.length > 0) {
          const superAdminRoleIds = superAdminRoles.map(r => r.id);

          // Get all users with super_admin role
          const { data: superAdminUsers, error: usersErr } = await supabase
            .from('profile_table')
            .select('id')
            .in('row_id', superAdminRoleIds);

          if (!usersErr && superAdminUsers && superAdminUsers.length > 0) {
            const superAdminUserIds = superAdminUsers.map(u => u.id);
            // Include current user and all super_admin users
            const { data, error } = await supabase
              .from('role_table')
              .select('id, role_name')
              .in('created_by', [userId, ...superAdminUserIds])
              .order('role_name');

            if (error) throw error;
            console.log("Roles for user and super_admins:", data);
            setRoles((data || []).map(r => ({ id: r.id, name: r.role_name })));
          } else {
            // If no super_admin users found, just fetch current user's roles
            const { data, error } = await supabase
              .from('role_table')
              .select('id, role_name')
              .eq('created_by', userId)
              .order('role_name');

            if (error) throw error;
            console.log("Roles for user:", data);
            setRoles((data || []).map(r => ({ id: r.id, name: r.role_name })));
          }
        } else {
          // If super_admin role doesn't exist, just fetch current user's roles
          const { data, error } = await supabase
            .from('role_table')
            .select('id, role_name')
            .eq('created_by', userId)
            .order('role_name');

          if (error) throw error;
          console.log("Roles for user:", data);
          setRoles((data || []).map(r => ({ id: r.id, name: r.role_name })));
        }
      }
    } catch (err) {
      console.error('Failed to load roles:', err.message);
    }
  }

  async function fetchAllRoles() {
    try {
      const { data, error } = await supabase
        .from('role_table')
        .select('id, role_name')
        .order('role_name');
      if (error) throw error;
      console.log('All roles data:', data);
      setAllRoles((data || []).map(r => ({ id: r.id, name: r.role_name })));
      const defaultOwnerClient = (data || []).find(r => r.role_name?.toLowerCase() === 'owner - client');
      if (defaultOwnerClient) {
        setOwnerClientRoleId(defaultOwnerClient.id);
        if (formMode === 'create' && userType === 'client') {
          setFormData(prev => ({ ...prev, row_id: defaultOwnerClient.id }));
        }
      }

    } catch (err) {
      console.error('Failed to load all roles:', err.message);
    }
  }

  /* ================= FORM HANDLERS ================= */

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  }

  function resetForm() {
    setFormMode('create');
    setEditingId(null);
    setFormData({
      email: '',
      password: '',
      username: '',
      row_id: '',
      payment: '',
      organization_id: orgLocked ? formData.organization_id : '',
    });
    // Reset Org logic
    setIsNewOrg(false);
    setNewOrgName('');
    setValidationErrors({});

    setError(null);
    setSuccessMsg('');
    setShowModal(false);
  }

  function openCreateModal() {
    setFormMode('create');
    setUserType(orgLocked ? 'client' : 'internal');
    setFormData({
      email: '',
      password: '',
      username: '',
      row_id: '',
      payment: '',
      organization_id: orgLocked ? formData.organization_id : '',
    });
    setIsNewOrg(false);
    setNewOrgName('');
    setValidationErrors({});
    setShowModal(true);
  }

  function openEditModal(user) {
    setFormMode('edit');
    setEditingId(user.id);
    setUserType(user.organization_id ? 'client' : 'internal');
    setFormData({
      email: user.email || '',
      password: '',
      username: user.username,
      row_id: user.row_id,
      payment: user.payment,
      organization_id: user.organization_id || '',
    });
    setIsNewOrg(false);
    setNewOrgName('');
    setValidationErrors({});
    setShowModal(true);
  }

  /* ================= SUBMIT LOGIC ================= */

  async function handleSubmit(e) {
    e.preventDefault();
    setFormLoading(true);
    setError(null);
    setSuccessMsg('');

    try {
      let errors = {};
      if (!formData.email) errors.email = 'Email is required';
      if (formMode === 'create' && !formData.password) errors.password = 'Password is required';
      if (!formData.username) errors.username = 'Name is required';
      if (!formData.row_id) errors.row_id = 'Role is required';
      if (userType === 'client' && !orgLocked && !formData.payment) errors.payment = 'Payment Status is required';
      if (userType === 'client' && isNewOrg && !newOrgName.trim()) errors.organization_id = 'Organization Name is required';
      if (userType === 'client' && !isNewOrg && !orgLocked && !formData.organization_id) errors.organization_id = 'Organization is required';

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setFormLoading(false);
        return;
      }

      // 1️⃣ Handle Organization/Payment based on user type
      let finalOrgId = userType === 'client' ? formData.organization_id : null;
      let finalPayment = userType === 'client' ? formData.payment : null;
      const finalRowId = formData.row_id || null;

      // If org is locked (current user tied to org), keep that org and null payment for client users
      if (orgLocked && userType === 'client') {
        finalOrgId = formData.organization_id;
        finalPayment = null;
      }

      if (userType === 'client' && isNewOrg) {
        if (!newOrgName.trim()) throw new Error('Organization name is required');

        // Create the new organization first
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .insert({ name: newOrgName.trim() })
          .select()
          .single();

        if (orgError) throw orgError;

        // Use the newly created ID
        finalOrgId = orgData.id;

        // Update local list so we don't need to refresh page
        setOrganizations(prev => [...prev, orgData]);
      }

      // 2️⃣ Create or Update User
      if (formMode === 'create') {
        // Create auth user
        const { data, error: authError } = await supabase.auth.admin.createUser({
          email: formData.email.trim(),
          password: formData.password,
          email_confirm: true
        });

        if (authError) throw authError;
        if (!data.user) throw new Error('User creation failed');

        // Create profile linked to Org + Role (row_id)
        const { error: profileError } = await supabase
          .from('profile_table')
          .insert({
            id: data.user.id,
            username: formData.username.trim(),
            row_id: finalRowId,
            payment: finalPayment,
            organization_id: finalOrgId || null,
          });

        if (profileError) throw profileError;
        setSuccessMsg('User and Organization created successfully');

      } else {
        // UPDATE Existing User
        const { error } = await supabase
          .from('profile_table')
          .update({
            username: formData.username.trim(),
            row_id: finalRowId,
            payment: finalPayment,
            organization_id: finalOrgId || null,
          })
          .eq('id', editingId);

        if (error) throw error;

        // If a new password was provided, update via backend admin API
        if (formData.password && formData.password.trim().length > 0) {
          const pwdRes = await fetchWithAuth(`${API_BASE}/account/update-password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId: editingId, newPassword: formData.password }),
          });

          // Handle 401 - if fetchWithAuth failed to refresh
          if (pwdRes.status === 401) {
            console.warn("🔐 Token expired or invalid");
            localStorage.removeItem("userRole");
            localStorage.removeItem("userPermissions");
            window.location.href = "/login";
            return;
          }

          const pwdJson = await pwdRes.json();
          if (!pwdRes.ok) {
            throw new Error(pwdJson.error || 'Password update failed');
          }
        }

        setSuccessMsg('User updated successfully');
      }

      resetForm();
      fetchProfiles(false);
    } catch (err) {
      setError(err.message || 'Operation failed');
    } finally {
      setFormLoading(false);
    }
  }

  /* ================= UTILS ================= */

  function openDeleteModal(user) {
    setUserToDelete(user);
    setShowDeleteModal(true);
  }

  function closeDeleteModal() {
    setUserToDelete(null);
    setShowDeleteModal(false);
  }

  // async function handleDelete() {
  //   if (!userToDelete) return;

  //   setLoading(true);
  //   try {
  //     // Delete the auth user (this deletes the email/password from auth.users)
  //     // const { error: authError } = await supabase.auth.admin.deleteUser(userToDelete.id);
  //     // if (authError) throw authError;

  //     // Delete the profile (in case it wasn't cascaded)
  //     const { error: profileError } = await supabase.from('profile_table').delete().eq('id', userToDelete.id);
  //     if (profileError) {
  //       console.error('Profile deletion error (may already be cascaded):', profileError);
  //     }

  //     setSuccessMsg('User deleted successfully');
  //     fetchProfiles();
  //     closeDeleteModal();
  //   } catch (err) {
  //     setError(err.message || 'Delete failed');
  //   } finally {
  //     setLoading(false);
  //   }
  // }

  async function handleDelete() {
    if (!userToDelete) return;


    setFormLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_BASE}/account/soft-delete/${encodeURIComponent(userToDelete.id)}`,
        {
          method: 'DELETE',
        }
      );

      // Handle 401 - if fetchWithAuth failed to refresh
      if (res.status === 401) {
        console.warn("🔐 Token expired or invalid");
        localStorage.removeItem("userRole");
        localStorage.removeItem("userPermissions");
        window.location.href = "/login";
        return;
      }

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Delete failed');
      }

      const { error: profileError } = await supabase.from('profile_table').delete().eq('id', userToDelete.id);
      if (profileError) {
        console.error('Profile deletion error (may already be cascaded):', profileError);
      }
      setSuccessMsg('User deleted successfully');
      fetchProfiles(false);
      closeDeleteModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  }



  const getOrgName = (id) => {
    if (!id) return '-';
    const org = organizations.find(o => o.id === id);
    return org ? org.name : 'Unknown';
  };

  // const isFormValid = () => {
  //   // Required fields for both create and edit
  //   if (!formData.username || !formData.email) return false;

  //   // Create mode needs password
  //   // if (formMode === 'create' && !formData.password) return false;

  //   // Organization is required only for client type and if not locked
  //   if (userType === 'client' && !orgLocked && !formData.organization_id && !isNewOrg) return false;

  //   // If adding new org, name is required
  //   if (isNewOrg && !newOrgName.trim()) return false;

  //   // Payment is required only for client type and if not locked
  //   if (userType === 'client' && !orgLocked && !formData.payment) return false;

  //   return true;
  // };

  const getRoleName = (id) => {
    if (!id) return '-';
    const role = allRoles.find((r) => r.id === id);
    return role ? role.name : '-';
  };

  return (
    <div className="um-container">
      <div className="um-header">
        <h2 className="">User Management</h2>
        <button onClick={openCreateModal} className="btn btn-create">
          Create User
        </button>
      </div>

      {/* {error && !showModal && <div className="alert alert-error mb-4">{error}</div>}
      {successMsg && !showModal && <div className="alert alert-success mb-4">{successMsg}</div>} */}

      {/* TABLE */}
      <div className="um-table-wrapper">
        <table className="um-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>

              <th>Organization</th>

              <th>Role</th>

              {!orgLocked && (
                <th>Payment</th>
              )}
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={orgLocked ? 5 : 6} className="text-center" style={{ padding: '2rem' }}>
                  <LoadingSpinner message="Loading users…" fullScreen={false} />
                </td>
              </tr>
            ) : users.filter((u) => u.id !== currentUserId).length === 0 ? (
              <tr>
                <td colSpan={orgLocked ? 5 : 6} className="text-center" style={{ padding: '2rem', color: 'var(--text-secondary)' }}>
                  No users found
                </td>
              </tr>
            ) : (
              users.filter((u) => u.id !== currentUserId).map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.username}</td>
                  <td>{getOrgName(u.organization_id)}</td>

                  <td>{getRoleName(u.row_id)}</td>

                  {!orgLocked && (
                    <td>{u.payment || "-"}</td>)}
                  <td>
                    <div className="actions-cell">
                      <button onClick={() => openEditModal(u)} className="btn-edit">Edit</button>


                      <button
                        onClick={() => openDeleteModal(u)}
                        className="btn-delete"
                        disabled={u.row_id === ownerClientRoleId && !isSuperAdmin}
                        style={u.row_id === ownerClientRoleId && !isSuperAdmin ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container user-modal-container" style={{ maxWidth: '350px' }}>
            <div className="modal-body">
              <div className="modal-header">
                <h2 className="modal-title">Confirm Delete</h2>
              </div>
              <div style={{ padding: '20px 0' }}>
                <p>Are you sure you want to delete this user?</p>
                {userToDelete && (
                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <p style={{ margin: '5px 0' }}><strong>Email:</strong> {userToDelete.email}</p>
                    <p style={{ margin: '5px 0' }}><strong>Name:</strong> {userToDelete.username}</p>
                  </div>
                )}
                <p style={{ marginTop: '15px', color: '#d32f2f', fontWeight: '500' }}>This action cannot be undone.</p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="btn-cancel"
                  disabled={formLoading}
                >
                  No, Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="btn-delete"
                  disabled={formLoading}
                  style={{ minWidth: '100px' }}
                >
                  {formLoading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container user-modal-container">
            <div className="modal-body">
              <div className="modal-header">
                <h2 className="modal-title">
                  {formMode === 'create' ? 'Create New User' : 'Edit User'}
                </h2>
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              <form onSubmit={handleSubmit} className="form-grid" noValidate>

                {/* User Type Toggle (hidden when org is locked) */}
                {!orgLocked && (
                  <div className="form-group col-span-2">
                    <label>User Type</label>
                    <div style={{ display: 'flex', gap: '32px', marginTop: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="userType"
                          value="internal"
                          checked={userType === 'internal'}
                          onChange={() => {
                            setUserType('internal');
                            setIsNewOrg(false);
                            setNewOrgName('');
                            setValidationErrors({});
                            setFormData(p => ({ ...p, organization_id: '', payment: '', row_id: '' }));
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', margin: '0' }}
                        />
                        <span style={{ lineHeight: '1', margin: '0' }}>Internal</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="userType"
                          value="client"
                          checked={userType === 'client'}
                          onChange={() => {
                            setUserType('client');
                            setValidationErrors({});
                            if (ownerClientRoleId) {
                              setFormData(p => ({ ...p, row_id: ownerClientRoleId }));
                            }
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', margin: '0' }}
                        />
                        <span style={{ lineHeight: '1', margin: '0' }}>Client</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Organization Field with Toggle (client only) */}
                {userType === 'client' && (
                  <div className="form-group col-span-2">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label>Organization</label>
                      {!orgLocked && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsNewOrg(!isNewOrg);
                            setValidationErrors({});
                            setFormData(p => ({ ...p, organization_id: '' }));
                            setNewOrgName('');
                          }}
                          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
                        >
                          {isNewOrg ? 'Select Existing' : 'Enter New Organization'}
                        </button>
                      )}
                    </div>

                    {isNewOrg && !orgLocked ? (
                      <>
                        <input
                          type="text"
                          placeholder="Enter new organization name"
                          value={newOrgName}
                          onChange={(e) => setNewOrgName(e.target.value)}
                          className={`form-input ${validationErrors.organization_id ? 'error' : ''}`}
                        />
                        {validationErrors.organization_id && <div className="validation-error">{validationErrors.organization_id}</div>}
                      </>
                    ) : (
                      <>
                        <select
                          name="organization_id"
                          value={formData.organization_id}
                          onChange={handleChange}
                          className={`form-select ${validationErrors.organization_id ? 'error' : ''}`}
                          disabled={orgLocked}
                        >
                          <option value="">Select Organization</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                        {validationErrors.organization_id && <div className="validation-error">{validationErrors.organization_id}</div>}
                      </>
                    )}
                  </div>
                )}

                {/* Email */}
                <div className="form-group">
                  <label>Email</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={formMode === 'edit'}
                    className={`form-input ${validationErrors.email ? 'error' : ''}`}
                  />
                  {validationErrors.email && <div className="validation-error">{validationErrors.email}</div>}
                </div>

                {/* Password */}
                <div className="form-group">
                  <label>Password</label>
                  <div className="password-wrapper">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={formMode === 'edit' ? 'Leave blank to keep current password' : 'Password'}
                      value={formData.password}
                      onChange={handleChange}
                      className={`form-input ${validationErrors.password ? 'error' : ''}`}
                    />
                    <span className="password-toggle-icon" onClick={() => setShowPassword(v => !v)}>
                      {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                    </span>
                  </div>
                  {validationErrors.password && <div className="validation-error">{validationErrors.password}</div>}
                </div>

                {/* Username */}
                <div className="form-group">
                  <label>Name</label>
                  <input
                    name="username"
                    placeholder="Name"
                    value={formData.username}
                    onChange={handleChange}
                    className={`form-input ${validationErrors.username ? 'error' : ''}`}
                  />
                  {validationErrors.username && <div className="validation-error">{validationErrors.username}</div>}
                </div>

                {/* Role */}
                <div className="form-group">
                  <label>Role</label>
                  <select
                    name="row_id"
                    value={formData.row_id}
                    onChange={handleChange}
                    className={`form-select ${validationErrors.row_id ? 'error' : ''}`}
                    disabled={!orgLocked && userType === 'client' && ownerClientRoleId}
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.row_id && <div className="validation-error">{validationErrors.row_id}</div>}
                  {!orgLocked && userType === 'client' && ownerClientRoleId && (
                    <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                      Client users are automatically assigned "owner - client" role
                    </small>
                  )}
                </div>

                {/* Payment (client only) */}
                {userType === 'client' && !orgLocked && (
                  <div className="form-group">
                    <label>Payment Status</label>
                    <select
                      name="payment"
                      value={formData.payment}
                      onChange={handleChange}
                      className={`form-select ${validationErrors.payment ? 'error' : ''}`}
                    >
                      <option value="">Select Status</option>
                      <option value="free_trial">Free Trial</option>
                      <option value="paid">Paid</option>
                      <option value="expired">Expired</option>
                    </select>
                    {validationErrors.payment && <div className="validation-error">{validationErrors.payment}</div>}
                  </div>
                )}

                {/* Actions */}
                <div className="col-span-2 form-actions">
                  <button type="submit" className="btn btn-submit">
                    {formLoading ? (
                      formMode === 'create' ? 'Creating...' : 'Updating...'
                    ) : (
                      formMode === 'create' ? 'Create' : 'Update'
                    )}
                  </button>
                  <button type="button" onClick={resetForm} className="btn btn-cancel">
                    Cancel
                  </button>
                </div>

              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;